import React from 'react';
import { Modal, Button, Table, Badge, Row, Col } from 'react-bootstrap';
import { Icon } from '@iconify/react';

const PatientInvoiceDetailModal = ({ show, onHide, invoice, onEmail }) => {
    if (!invoice) return null;

    const handlePrint = () => {
        window.print();
    };

    const handleSendEmail = () => {
        if (onEmail) {
            onEmail(invoice._id);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
    };

    const formatTime = (dateString) => {
        if (!dateString) return '';
        return new Date(dateString).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    };

    const cleanDoctorName = (name) => {
        if (!name) return 'Unknown';
        // Remove common prefixes case-insensitive, handling both "Dr. Name" and "Dr.Name"
        return name.replace(/^(dr\.?|Dr\.?|mr\.?|mrs\.?|ms\.?|miss\.?)\s*/i, '').trim();
    };

    return (
        <Modal show={show} onHide={onHide} size="lg" centered className="patient-invoice-modal" enforceFocus={false}>
            <Modal.Header closeButton className="border-bottom-0 bg-white d-print-none">
                {/* No Title */}
            </Modal.Header>
            <Modal.Body className="p-0 bg-white">
                <div className="p-5">
                    {/* 1. Header: Clinic Branding & Status */}
                    <div className="d-flex justify-content-between align-items-start mb-5 border-bottom pb-4">
                        <div className="d-flex align-items-center gap-3">
                            <div className="bg-primary-50 text-primary p-3 rounded-circle border border-primary-100 d-flex align-items-center justify-content-center" style={{ width: '64px', height: '64px' }}>
                                <Icon icon="solar:hospital-bold" className="display-6" />
                            </div>
                            <div>
                                <h3 className="fw-bolder text-dark mb-0">{invoice.clinicId?.name || 'Clinic Name'}</h3>
                                <p className="text-secondary text-sm mb-0">{invoice.clinicId?.address}</p>
                                {invoice.clinicId?.contactNumber && (
                                    <p className="text-secondary text-sm mb-0"><Icon icon="solar:phone-linear" className="me-1" />{invoice.clinicId.contactNumber}</p>
                                )}
                            </div>
                        </div>
                        <div className="text-end">
                            <h4 className="fw-bold text-dark mb-1">CONSULTATION INVOICE</h4>
                            <p className="text-secondary fw-medium mb-2">Ref: #{invoice.invoiceNumber}</p>
                            <Badge bg={invoice.status === 'Paid' ? 'success' : 'warning'} className="px-3 py-2 rounded-pill text-uppercase text-xs fw-bold">
                                {invoice.status}
                            </Badge>
                        </div>
                    </div>

                    {/* 2. Info Grid: Patient & Appointment Details */}
                    <div className="row g-4 mb-5">
                        {/* Patient Details */}
                        <div className="col-md-6">
                            <div className="h-100 p-4 bg-light rounded-4 border border-light-subtle">
                                <h6 className="text-uppercase text-secondary text-xs fw-bolder mb-3 d-flex align-items-center gap-2">
                                    <Icon icon="solar:user-circle-bold" /> Patient Details
                                </h6>
                                <h5 className="fw-bold text-dark mb-2">{invoice.patientDetails?.name || invoice.patientId?.name || invoice.customerInfo?.name || 'Guest'}</h5>
                                <div className="d-flex flex-column gap-2 text-sm text-secondary-dark">

                                    <div className="d-flex justify-content-between border-bottom border-secondary-subtle pb-1">
                                        <span className="fw-medium text-secondary">Phone:</span>
                                        <span className="fw-bold text-dark">{invoice.patientDetails?.phone || invoice.patientId?.phone || 'N/A'}</span>
                                    </div>
                                    <div className="d-flex justify-content-between">
                                        <span className="fw-medium text-secondary">Email:</span>
                                        <span className="fw-bold text-dark">{invoice.patientDetails?.email || invoice.patientId?.email || 'N/A'}</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Appointment/Doctor Details */}
                        <div className="col-md-6">
                            <div className="h-100 p-4 bg-light rounded-4 border border-light-subtle">
                                <h6 className="text-uppercase text-secondary text-xs fw-bolder mb-3 d-flex align-items-center gap-2">
                                    <Icon icon="solar:stethoscope-bold" /> Appointment Details
                                </h6>
                                <div className="d-flex flex-column gap-2 text-sm">
                                    <div className="d-flex justify-content-between border-bottom border-secondary-subtle pb-1">
                                        <span className="fw-medium text-secondary">Doctor:</span>
                                        <span className="fw-bold text-dark">Dr. {cleanDoctorName(invoice.doctorDetails?.name || invoice.doctorId?.name)}</span>
                                    </div>
                                    <div className="d-flex justify-content-between border-bottom border-secondary-subtle pb-1">
                                        <span className="fw-medium text-secondary">Specialization:</span>
                                        <span className="fw-bold text-dark">{invoice.doctorDetails?.specialization || invoice.doctorId?.specialization || 'General'}</span>
                                    </div>
                                    <div className="d-flex justify-content-between border-bottom border-secondary-subtle pb-1">
                                        <span className="fw-medium text-secondary">Date:</span>
                                        <span className="fw-bold text-dark">{formatDate(invoice.appointmentId?.date || invoice.date || invoice.createdAt)}</span>
                                    </div>
                                    <div className="d-flex justify-content-between">
                                        <span className="fw-medium text-secondary">Time (Slot):</span>
                                        <span className="fw-bold text-dark">{invoice.appointmentId?.timeSlot || formatTime(invoice.date || invoice.createdAt) || 'N/A'}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* 3. Services Table */}
                    <div className="mb-5">
                        <h6 className="text-uppercase text-secondary text-xs fw-bolder mb-3 ps-1">Services & Charges</h6>
                        <div className="table-responsive rounded-4 border shadow-sm overflow-hidden">
                            <Table className="mb-0">
                                <thead className="bg-base border-bottom">
                                    <tr>
                                        <th className="py-3 px-4 text-secondary text-xs text-uppercase fw-bold">Service Description</th>
                                        <th className="py-3 px-4 text-end text-secondary text-xs text-uppercase fw-bold">Amount</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white">
                                    {invoice.items && invoice.items.length > 0 ? (
                                        invoice.items.map((item, idx) => (
                                            <tr key={idx}>
                                                <td className="px-4 py-3 fw-medium text-dark align-middle">{item.description || 'Consultation Service'}</td>
                                                <td className="px-4 py-3 text-end fw-bold text-dark align-middle">{invoice.currency} {Number(item.total || item.amount || invoice.amount).toLocaleString()}</td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td className="px-4 py-3 fw-medium text-dark">Medical Consultation Fee</td>
                                            <td className="px-4 py-3 text-end fw-bold text-dark">{invoice.currency} {Number(invoice.amount).toLocaleString()}</td>
                                        </tr>
                                    )}
                                </tbody>
                                <tfoot className="bg-light border-top">
                                    <tr>
                                        <td className="px-4 py-3 text-end fw-bold text-secondary text-uppercase text-xs" style={{ letterSpacing: '1px' }}>Total Amount</td>
                                        <td className="px-4 py-3 text-end fw-bolder text-primary h5 mb-0">{invoice.currency} {Number(invoice.amount).toLocaleString()}</td>
                                    </tr>
                                </tfoot>
                            </Table>
                        </div>
                    </div>

                    {/* 4. Footer Note & Creator Info */}
                    <div className="d-flex justify-content-between align-items-center pt-4 border-top border-dashed text-secondary text-xs">
                        <div>
                            <p className="mb-0 fw-bold">Receipt generated by {invoice.clinicId?.name}</p>
                            <p className="mb-0">Valid proof of payment for medical services.</p>
                        </div>
                        <div className="text-end">
                            <p className="mb-0">Created By:</p>
                            <p className="fw-bold text-dark mb-0">{invoice.assistantDetails?.name || invoice.createdBy?.name || 'System'}</p>
                        </div>
                    </div>

                </div>
            </Modal.Body>
            <Modal.Footer className="border-top-0 bg-white p-4 justify-content-center d-print-none">
                <Button variant="light" onClick={onHide} className="px-4 fw-bold rounded-3">Close</Button>
                <Button variant="outline-dark" onClick={handlePrint} className="d-flex align-items-center gap-2 px-4 fw-bold rounded-3">
                    <Icon icon="solar:printer-bold" /> Print / Download
                </Button>
                <Button variant="primary" onClick={handleSendEmail} className="d-flex align-items-center gap-2 px-4 fw-bold rounded-3 shadow-sm icon-link-hover">
                    <Icon icon="solar:letter-bold" /> Email Receipt
                </Button>
            </Modal.Footer>

            {/* Print Styles */}
            <style>{`
                @media print {
                    body * { visibility: hidden; }
                    .patient-invoice-modal .modal-content, .patient-invoice-modal .modal-content * { visibility: visible; }
                    .patient-invoice-modal .modal-content { position: absolute; left: 0; top: 0; width: 100%; border: none; box-shadow: none; padding: 0 !important; }
                    .modal-footer, .btn-close { display: none !important; }
                    .modal-body { padding: 0 !important; background: white !important; -webkit-print-color-adjust: exact; }
                    .bg-light { background-color: #f8f9fa !important; }
                    .bg-primary-50 { background-color: #eff6ff !important; color: #000 !important; }
                    .badge { border: 1px solid #000; color: #000 !important; }
                }
            `}</style>
        </Modal>
    );
};

export default PatientInvoiceDetailModal;
